/**
 * AudienceReachCard.jsx — v2.9.172 (Sprint 2: UI Integration)
 *
 * Visualises the 3-tier audience reach estimate returned by
 *   GET /api/audience/reach/{station_id}?date=YYYY-MM-DD
 *
 * Two visual variants:
 *   • variant="light"  → Stream Monitor (light/dark themed, gray surface)
 *   • variant="dark"   → Songs Analyzer (glassmorphic, white-on-dark)
 *
 * Renders:
 *   - Tier badge (🟢 Πραγματικά / 🟡 Μοντέλο / 🟠 Στατιστικά)
 *   - Big daily-unique number with confidence range
 *   - Secondary stats: ASL (min), TLH, peak, avg, capacity%
 *   - Notes list (caveats from outlier guards / data gaps)
 *   - "Πώς υπολογίζεται;" button → methodology modal
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, Clock, Activity, Info, Loader2, AlertCircle,
  TrendingUp, X, BarChart3, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  1: {
    label: 'Πραγματικά Δεδομένα',
    color: 'emerald',
    dot: 'bg-emerald-500',
    icon: '🟢',
    confidence: '±5 %',
  },
  2: {
    label: 'Εκτίμηση Μοντέλου',
    color: 'amber',
    dot: 'bg-amber-500',
    icon: '🟡',
    confidence: '±15–25 %',
  },
  3: {
    label: 'Στατιστική Εκτίμηση',
    color: 'orange',
    dot: 'bg-orange-500',
    icon: '🟠',
    confidence: '±30–50 %',
  },
};

const styleFor = (variant) => {
  if (variant === 'dark') {
    return {
      card: 'bg-white/5 border border-white/10 rounded-xl p-4',
      heading: 'text-white',
      subText: 'text-white/60',
      labelText: 'text-white/50',
      valueText: 'text-white',
      tierBgBase: 'bg-white/10',
      divider: 'border-white/10',
      hint: 'text-white/45',
      methodBtn: 'text-white/70 hover:text-white border-white/20 hover:bg-white/10',
      noteBox: 'bg-amber-500/10 border border-amber-500/30 text-amber-200',
      modalBg: 'bg-slate-900',
      modalBorder: 'border-white/15',
      modalText: 'text-white',
      modalSubText: 'text-white/70',
      modalCloseBtn: 'text-white/60 hover:text-white',
    };
  }
  // light variant (Stream Monitor) — supports tailwind dark mode classes
  return {
    card: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4',
    heading: 'text-gray-900 dark:text-gray-100',
    subText: 'text-gray-600 dark:text-gray-400',
    labelText: 'text-gray-500 dark:text-gray-400',
    valueText: 'text-gray-900 dark:text-gray-100',
    tierBgBase: 'bg-gray-100 dark:bg-gray-700/50',
    divider: 'border-gray-200 dark:border-gray-700',
    hint: 'text-gray-500 dark:text-gray-400',
    methodBtn: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600',
    noteBox: 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300',
    modalBg: 'bg-white dark:bg-gray-900',
    modalBorder: 'border-gray-200 dark:border-gray-700',
    modalText: 'text-gray-900 dark:text-gray-100',
    modalSubText: 'text-gray-700 dark:text-gray-300',
    modalCloseBtn: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
  };
};

const formatNum = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('el-GR').format(Math.round(n));
};

const TierBadge = ({ tier, variant }) => {
  const meta = TIER_META[tier] || TIER_META[3];
  const colorMap = {
    emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    amber:   'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
    orange:  'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40',
  };
  const cls = colorMap[meta.color] || colorMap.orange;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}
      data-testid={`audience-reach-tier-${tier}`}
      title={`${meta.label} · Εμπιστοσύνη ${meta.confidence}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const MethodologyModal = ({ open, onClose, token, variant }) => {
  const s = styleFor(variant);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDoc = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/audience/reach/methodology`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) setDoc(await r.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open && !doc) loadDoc();
  }, [open, doc, loadDoc]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4"
      data-testid="audience-reach-methodology-modal"
      onClick={onClose}
    >
      <div
        className={`max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-xl border shadow-2xl ${s.modalBg} ${s.modalBorder}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${s.modalBorder}`}>
          <div>
            <h3 className={`text-base font-semibold flex items-center gap-2 ${s.modalText}`}>
              <BarChart3 className="w-4 h-4" />
              Πώς υπολογίζεται;
            </h3>
            <p className={`text-xs ${s.modalSubText} mt-0.5`}>
              {doc?.version ? `Audience Reach Estimator ${doc.version}` : 'Audience Reach Estimator'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded ${s.modalCloseBtn}`}
            data-testid="audience-reach-methodology-close"
            aria-label="Κλείσιμο"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className={`p-4 space-y-4 ${s.modalSubText}`}>
          {loading && (
            <div className="flex items-center gap-2 text-sm py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Φόρτωση...
            </div>
          )}

          {doc?.tiers?.map((t) => {
            const meta = TIER_META[t.tier] || TIER_META[3];
            const colorMap = {
              emerald: 'border-emerald-500/40 bg-emerald-500/5',
              amber:   'border-amber-500/40 bg-amber-500/5',
              orange:  'border-orange-500/40 bg-orange-500/5',
            };
            return (
              <div
                key={t.tier}
                className={`p-3 rounded-lg border ${colorMap[meta.color] || colorMap.orange}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                  <strong className={s.modalText}>Tier {t.tier} — {t.label}</strong>
                  <span className={`text-[11px] ml-auto ${s.hint}`}>Εμπιστοσύνη {t.confidence}</span>
                </div>
                <p className="text-sm mb-2">{t.description}</p>
                <code className={`block text-[11px] px-2 py-1 rounded ${s.tierBgBase} ${s.valueText}`}>
                  {t.formula}
                </code>
              </div>
            );
          })}

          {doc?.default_asl_minutes && (
            <div className="space-y-1.5">
              <div className={`text-xs font-semibold ${s.modalText}`}>
                Default ASL ανά music_type (Tier 3)
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {Object.entries(doc.default_asl_minutes).map(([k, v]) => (
                  <div key={k} className={`flex justify-between px-2 py-1 rounded ${s.tierBgBase}`}>
                    <span className={s.subText}>{k}</span>
                    <span className={`font-medium ${s.valueText}`}>{v}′</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {doc?.outlier_guards && (
            <div className="space-y-1.5">
              <div className={`text-xs font-semibold ${s.modalText}`}>Outlier guards</div>
              <ul className={`text-xs list-disc list-inside space-y-0.5 ${s.subText}`}>
                <li>ASL &gt; {doc.outlier_guards.asl_max_minutes / 60} ώρες → αγνοείται (zombie connection)</li>
                <li>ASL &lt; {doc.outlier_guards.asl_min_minutes} min → αγνοείται (bot scraping)</li>
                <li>Unique &gt; capacity × {doc.outlier_guards.capacity_overflow_cap} → cap στο capacity</li>
                <li>Peak ≥ {doc.outlier_guards.saturated_pct_warning}% capacity → warning &quot;saturated&quot;</li>
              </ul>
            </div>
          )}

          {doc?.notes && (
            <ul className={`text-[11px] list-disc list-inside space-y-0.5 ${s.hint}`}>
              {doc.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, unit, s, tooltip }) => (
  <div
    className={`flex flex-col gap-0.5 px-3 py-2 rounded-lg ${s.tierBgBase} ${tooltip ? 'cursor-help' : ''}`}
    title={tooltip || undefined}
    data-testid={`audience-reach-tile-${String(label).toLowerCase().replace(/\W+/g, '-')}`}
  >
    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${s.labelText}`}>
      <Icon className="w-3 h-3" /> {label}
      {tooltip && <Info className="w-2.5 h-2.5 opacity-50" />}
    </div>
    <div className={`text-sm font-semibold ${s.valueText}`}>
      {value}{unit ? <span className={`ml-0.5 text-[11px] font-normal ${s.subText}`}>{unit}</span> : null}
    </div>
  </div>
);

const AudienceReachCard = ({
  stationId,
  token,
  date,
  variant = 'light',
  className = '',
  compact = false,
  userFacing = false,
}) => {
  const s = styleFor(variant);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);

  const fetchData = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    setError(null);
    try {
      const q = date ? `?date=${date}` : '';
      const r = await fetch(`${API}/api/audience/reach/${stationId}${q}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        setData(null);
      } else {
        setData(await r.json());
      }
    } catch (e) {
      setError(e.message || 'fetch_failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [stationId, date, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={`${s.card} ${className}`} data-testid="audience-reach-loading">
        <div className={`flex items-center gap-2 text-sm ${s.subText}`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Υπολογισμός εκτίμησης ακροατών...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`${s.card} ${className}`} data-testid="audience-reach-error">
        <div className={`flex items-center gap-2 text-sm ${s.subText}`}>
          <AlertCircle className="w-4 h-4 text-amber-500" />
          Δεν ήταν δυνατός ο υπολογισμός εκτίμησης ακροατών
          {error ? <span className="ml-1 text-xs opacity-60">({error})</span> : null}
          <button
            onClick={fetchData}
            className={`ml-auto p-1 rounded ${s.methodBtn} border`}
            data-testid="audience-reach-retry"
            title="Επανάληψη"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  const tier = data.tier;
  const range = data.confidence_range || [];
  const hasRange = range[0] !== null && range[1] !== null;

  return (
    <>
      <div
        className={`${s.card} ${className}`}
        data-testid="audience-reach-card"
        data-station-id={stationId}
        data-tier={tier}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Users className={`w-4 h-4 ${s.subText}`} />
            <div>
              <div className={`text-sm font-semibold ${s.heading}`}>
                Εκτιμώμενοι Μοναδικοί Ακροατές
              </div>
              <div className={`text-[11px] ${s.hint}`}>
                {data.station_name || '—'} · {data.date}
              </div>
            </div>
          </div>
          <TierBadge tier={tier} variant={variant} />
        </div>

        {/* Big number + range */}
        <div className={`flex items-end gap-3 pb-3 border-b ${s.divider}`}>
          <div>
            <div className={`text-3xl font-bold leading-none ${s.heading}`} data-testid="audience-reach-daily-unique">
              {formatNum(data.daily_unique)}
            </div>
            <div className={`text-[11px] mt-1 ${s.hint}`}>μοναδικοί / ημέρα</div>
          </div>
          {hasRange && (
            <div className={`pb-1 text-[11px] ${s.subText}`} data-testid="audience-reach-confidence-range">
              <span className={s.labelText}>Εύρος εμπιστοσύνης</span>
              <div className={`font-medium ${s.valueText}`}>
                {formatNum(range[0])} – {formatNum(range[1])}
              </div>
            </div>
          )}
        </div>

        {/* Secondary stats */}
        {!compact && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <StatTile
              icon={Clock} label="ASL / day"
              value={data.asl_minutes != null ? data.asl_minutes : '—'}
              unit="min" s={s}
              tooltip="Average Session Length / day — συνολικός χρόνος (σε λεπτά) που μένει συνδεδεμένος ένας μοναδικός ακροατής μέσα στην ημέρα, αθροιστικά από όλα του τα sessions. (Πηγή: stream server)"
            />
            <StatTile
              icon={Activity} label="TLH"
              value={data.tlh != null ? formatNum(data.tlh) : '—'}
              unit="h" s={s}
              tooltip="Total Listener Hours — συνολικές ώρες ακρόασης από όλο το κοινό του σταθμού στην ημέρα."
            />
            <StatTile
              icon={TrendingUp} label="Peak"
              value={data.peak != null ? formatNum(data.peak) : '—'}
              s={s}
              tooltip="Μέγιστος αριθμός ταυτόχρονων ακροατών — η υψηλότερη τιμή που παρατηρήθηκε στη διάρκεια της ημέρας."
            />
            <StatTile
              icon={Users} label="Μ.Ο."
              value={data.avg != null ? formatNum(data.avg) : '—'}
              s={s}
              tooltip="Μέσος όρος ταυτόχρονων ακροατών στη διάρκεια της ημέρας."
            />
          </div>
        )}

        {/* Source / methodology line — admin view shows technical source; user-facing shows RadioApps brand */}
        <div className={`mt-3 text-[11px] flex items-start gap-1.5 ${s.subText}`}>
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span data-testid="audience-reach-source-label">
            {userFacing ? 'Δεδομένα: RadioApps Analytics' : data.source_label}
          </span>
        </div>

        {/* Notes (caveats) — admin only; user-facing hides technical caveats */}
        {!userFacing && data.notes && data.notes.length > 0 && (
          <div className={`mt-2 p-2 rounded text-[11px] ${s.noteBox}`} data-testid="audience-reach-notes">
            <ul className="list-disc list-inside space-y-0.5">
              {data.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}

        {/* Methodology button — admin only (Stream Monitor); hidden in user-facing Songs Analyzer view */}
        {!userFacing && (
          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() => setShowMethodology(true)}
              className={`text-[11px] px-2.5 py-1 rounded border ${s.methodBtn}`}
              data-testid="audience-reach-methodology-btn"
            >
              Πώς υπολογίζεται;
            </button>
            <span className={`text-[10px] ${s.hint}`}>
              {data.cluster_tag ? `cluster: ${data.cluster_tag}` : null}
            </span>
          </div>
        )}
      </div>

      {!userFacing && (
        <MethodologyModal
          open={showMethodology}
          onClose={() => setShowMethodology(false)}
          token={token}
          variant={variant}
        />
      )}
    </>
  );
};

export default AudienceReachCard;
